import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  agentToolsApi,
  type PaginatedMCPConnectionsResponse,
} from "../../../services/agent-tools-service";
import { mcpQueryKeys } from "../query-keys";
import { canNotifyMCPMutation, useScopedMCPMutation } from "./useScopedMCPMutation";
import { mcpRequestOptions } from "./useMCPQueryScope";

type ApiError = {
  response?: {
    data?: { error?: { message?: string }; detail?: string; message?: string };
  };
  message?: string;
};

const extractErrorMessage = (error: ApiError, fallback: string): string => {
  const data = error.response?.data;
  return (
    data?.error?.message ||
    data?.detail ||
    data?.message ||
    error.message ||
    fallback
  );
};

type Options = {
  onSuccess?: (id: string) => void;
};

export const useDeleteMCPConnection = (options: Options = {}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useScopedMCPMutation<void, string, undefined, ApiError>({
    mutationFn: (id, scope) => agentToolsApi.deleteMCPConnection(id, mcpRequestOptions(scope)),
    onSuccess: (_data, id, scope) => {
      if (canNotifyMCPMutation(scope)) toast.success(t("mcpPage.mutation.deleted"));

      // 1) Drop the deleted row from every cached list page synchronously so
      //    its <MCPListItem> unmounts on the next render and its per-id test
      //    observer detaches — no chance to fire a stale POST.
      queryClient.setQueriesData<PaginatedMCPConnectionsResponse>(
        { queryKey: mcpQueryKeys.lists(scope.key) },
        (old) => {
          if (!old || !Array.isArray(old.items)) return old;
          return {
            ...old,
            items: old.items.filter((item) => item.id !== id),
          };
        },
      );

      // 2) Let the page react (close the drawer if it was on the deleted id)
      //    before any further cache touches — this is what disables the
      //    drawer's per-id test/detail queries.
      if (canNotifyMCPMutation(scope)) options.onSuccess?.(id);

      // 3) Refetch the list authoritatively. Scoped to list pages so the
      //    per-id queries are never marked stale.
      queryClient.invalidateQueries({ queryKey: mcpQueryKeys.lists(scope.key) });

      // 4) Evict the now-orphaned per-id caches for the deleted connection.
      queryClient.removeQueries({
        queryKey: mcpQueryKeys.test(scope.key, id),
      });
      queryClient.removeQueries({
        queryKey: mcpQueryKeys.detail(scope.key, id),
      });
      queryClient.removeQueries({ queryKey: mcpQueryKeys.tools(scope.key, id) });
    },
    onError: (error: ApiError, _id, _context, scope) => {
      if (!canNotifyMCPMutation(scope, error)) return;
      toast.error(extractErrorMessage(error, t("mcpPage.mutation.deleteFailed")));
    },
  });
};
